import React from "react";
import { Tooltip } from "@mui/material";
import { GridEditInputCell } from "@mui/x-data-grid";

import GridViewIcon from "@mui/icons-material/GridView";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import FilePresentIcon from "@mui/icons-material/FilePresent";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PublicIcon from "@mui/icons-material/Public";
import SchoolIcon from "@mui/icons-material/School";
import SourceIcon from "@mui/icons-material/Source";

const datasets = {
  id: "datasets",
  label: "Datasets",
  endpoint: "datasets",
  // these columns are present in the data and **should** be displayed
  columnConfig: [
    {
      field: "activity_name",
      headerName: "Activity Name",
      width: 250,
      description: "",
      editable: true,
    },
    {
      field: "ngwos_basin",
      headerName: "NGWOS Basin",
      width: 110,
      description: "Basin name abbreviation",
      editable: true,
      renderCell: (params) => {
        // Access the task object from params
        const basin = params.row.basin;
        // Return a custom component or a simple string that combines the name and id
        return basin?.name || "";
      },
      renderEditCellProps: {
        categoriesEndpoint: "basins",
        categoriesEndpointResponseKey: "basins",
        categoriesEndpointResponseOptionsKey: "name",
        entityToUpdate: "datasets",
        entityColumnToUpdate: "basin_id",
      },
      // type: "singleSelect", // when singleSelect is type, then operator should be set to "is" in filterOptions
      // valueOptions: ["IRB", "DRB", "UCRB", "WRB", "TSRB"],
    },
    {
      field: "name",
      headerName: "Name",
      width: 200,
      description: "Name of dataset (e.g. Climgrid, NLCD, etc.)",
      editable: true,
    },
    {
      field: "description",
      headerName: "Description",
      width: 200,
      description:
        "Brief description of the dataset. If there are other versions of the dataset that exist, please include links to their catalog entry and indicate the key differences between those versions and this version in the description.",
      editable: true,
      preProcessEditCellProps: (params) => {
        const errorMessage =
          params.props.value?.length > 10000 ? "Limit 10000 characters" : null;
        return { ...params.props, error: errorMessage };
      },
      renderEditCell: (props) => {
        const { error } = props;
        return (
          <Tooltip open={!!error} title={error}>
            <GridEditInputCell {...props} />
          </Tooltip>
        );
      },
    },
    {
      field: "project",
      headerName: "Project",
      width: 200,
      description: "Project",
      editable: true,
      renderCell: (params) => {
        const project = params.row.project;
        return project?.name || "";
      },
      valueFormatter: (params) => {
        return params.value ? params.value.name : "";
      },
      renderEditCellProps: {
        categoriesEndpoint: "projects",
        categoriesEndpointResponseKey: "projects",
        categoriesEndpointResponseOptionsKey: "name",
        entityToUpdate: "datasets",
        entityColumnToUpdate: "project_id",
      },
    },
    {
      field: "activity_id",
      headerName: "Activity ID",
      width: 80,
      description: "Basin Activity ID",
      editable: true,
    },
    {
      field: "inventory_ready",
      headerName: "Inventory Ready",
      width: 150,
      description:
        "Indicator if dataset is going into inventory - do not fill in",
      editable: false,
    },

    {
      field: "source",
      headerName: "Source",
      width: 100,
      description: "Source of dataset (e.g. NASA, NOAA, USGS, etc.)",
      editable: true,
    },
    {
      field: "institutions",
      headerName: "Institution",
      width: 200,
      description: "Source of dataset (e.g. NASA, NOAA, USGS, etc.)",
      editable: true,
      renderCell: (params) => {
        const institutions = params.row.institutions;
        return institutions?.map((v) => v.name).join(", ") || "";
      },
      valueFormatter: (params) => {
        return (params.value || [])?.map((v) => v.name).join(", ") || "";
      },
      renderEditCellProps: {
        multiple: true,
        categoriesEndpoint: "institutions",
        categoriesEndpointResponseKey: "institutions",
        categoriesEndpointResponseOptionsKey: "name",
      },
    },

    {
      field: "release_status",
      headerName: "Release Status",
      width: 200,
      description: "What is the status of the development of this dataset?",
      editable: true,
      renderEditCellProps: {
        categoriesEndpoint: "release_statuses",
        categoriesEndpointResponseKey: "release_statuses",
        categoriesEndpointResponseOptionsKey: "name",
        entityToUpdate: "datasets",
        entityColumnToUpdate: "release_status_id",
      },
    },
    {
      field: "release_quarter",
      headerName: "Release Quarter",
      width: 200,
      description:
        "Expected quarter for release (optional, only fill in if dataset is in development)",
      editable: true,
    },
    {
      field: "domains",
      headerName: "Domains",
      width: 200,
      description: "Domain(s) of dataset variables.",
      editable: true,
      renderCell: (params) => {
        const domains = params.row.domains;
        return domains?.map((v) => v.name).join(", ") || "";
      },
      valueFormatter: (params) => {
        return (params.value || [])?.map((v) => v.name).join(", ") || "";
      },
      renderEditCellProps: {
        multiple: true,
        categoriesEndpoint: "domains",
        categoriesEndpointResponseKey: "domains",
        categoriesEndpointResponseOptionsKey: "name",
      },
    },
    {
      field: "variables",
      headerName: "Variables",
      width: 200,
      description: "Variables available in the dataset.",
      editable: true,
      renderCell: (params) => {
        const variableArray = params.row.variables;
        return variableArray?.map((v) => v.name).join(", ") || "";
      },
      valueFormatter: (params) => {
        return (params.value || [])?.map((v) => v.name).join(", ") || "";
      },
      renderEditCellProps: {
        multiple: true,
        categoriesEndpoint: "variables",
        categoriesEndpointResponseKey: "variables",
        categoriesEndpointResponseOptionsKey: "name",
      },
    },
    {
      field: "citation",
      headerName: "Citation",
      width: 150,
      description:
        "Published citation, following USGS guidelines: https://www.usgs.gov/data-management/data-citation",
      editable: true,
    },
    {
      field: "data_types",
      headerName: "Data Type",
      width: 200,
      description: "",
      editable: true,
      renderCell: (params) => {
        const dataTypes = params.row.data_types;
        return dataTypes?.map((v) => v.name).join(", ") || "";
      },
      valueFormatter: (params) => {
        return (params.value || [])?.map((v) => v.name).join(", ") || "";
      },
      renderEditCellProps: {
        multiple: true,
        categoriesEndpoint: "data_types",
        categoriesEndpointResponseKey: "data_types",
        categoriesEndpointResponseOptionsKey: "name",
      },
    },
    {
      field: "data_access_url",
      headerName: "Data Access URL",
      width: 150,
      description:
        "Published citation, following USGS guidelines: https://www.usgs.gov/data-management/data-citation",
      editable: true,
    },
    {
      field: "data_formats",
      headerName: "Data Format",
      width: 200,
      description: "File format of dataset (e.g. csv, netCDF, GeoTIFF, etc.)",
      editable: true,
      renderCell: (params) => {
        const dataFormats = params.row.data_formats;
        return dataFormats?.map((v) => v.name).join(", ") || "";
      },
      valueFormatter: (params) => {
        return (params.value || [])?.map((v) => v.name).join(", ") || "";
      },
      renderEditCellProps: {
        multiple: true,
        categoriesEndpoint: "data_formats",
        categoriesEndpointResponseKey: "data_formats",
        categoriesEndpointResponseOptionsKey: "name",
      },
    },
    {
      field: "spatial_extent",
      headerName: "Spatial Extent",
      width: 150,
      description:
        "Spatial extent of data (e.g. United States, super CONUS, OCONUS, CONUS, AK, HI, PR, VI, GU, North America, global, etc.)",
      editable: true,
    },
    {
      field: "spatial_resolution",
      headerName: "Spatial Resolution",
      width: 150,
      description:
        "Data layer resolution (e.g. 30 meter, 1:100,000, GF stream reaches, etc.)",
      editable: true,
    },
    {
      field: "temporal_coverage_start",
      headerName: "Temporal Coverage Start",
      width: 200,
      description: "Temporal coverage start date",
      editable: true,
    },
    {
      field: "temporal_coverage_end",
      headerName: "Temporal Coverage End",
      width: 200,
      description: "Temporal coverage end date",
      editable: true,
    },
    {
      field: "temporal_frequency",
      headerName: "Temporal Frequency",
      width: 200,
      description:
        "Temporal frequency of data (e.g. daily, 8-day, monthly etc.)",
      editable: true,
      renderEditCellProps: {
        categoriesEndpoint: "temporal_frequencies",
        categoriesEndpointResponseKey: "temporal_frequencies",
        categoriesEndpointResponseOptionsKey: "name",
        entityToUpdate: "datasets",
        entityColumnToUpdate: "temporal_frequency_id",
      },
    },
    {
      field: "update_type",
      headerName: "Update Type",
      width: 200,
      description:
        "What type of updates should we anticipate for this data product?",
      editable: true,
      renderEditCellProps: {
        categoriesEndpoint: "update_types",
        categoriesEndpointResponseKey: "update_types",
        categoriesEndpointResponseOptionsKey: "name",
        entityToUpdate: "datasets",
        entityColumnToUpdate: "update_type_id",
      },
    },
    {
      field: "update_frequency",
      headerName: "Update Frequency",
      width: 200,
      description:
        "Frequency at which the dataset is updated; if irregular, specify approximately how frequently the data gets updated (irregular - several times a year)",
      editable: true,
    },
    {
      field: "update_detail",
      headerName: "Update Detail",
      width: 200,
      description:
        "Describe the type of updates that happen to this dataset. A dataset that adds new locations or time steps should be tagged 'append'. A dataset where the existing data values are changed should be tagged 'modify'.",
      editable: true,
    },
    {
      field: "doi_url",
      headerName: "DOI URL",
      width: 150,
      description: "Digital object identifier url",
      editable: true,
    },
    {
      field: "documentation_url",
      headerName: "Documentation URL",
      width: 150,
      description: "URL to best human-readable documentation available",
      editable: true,
    },
    {
      field: "metadata_standard",
      headerName: "Metadata Standard",
      width: 200,
      description: "Metadata Standard",
      editable: true,
      renderEditCellProps: {
        categoriesEndpoint: "metadata_standards",
        categoriesEndpointResponseKey: "metadata_standards",
        categoriesEndpointResponseOptionsKey: "name",
        entityToUpdate: "datasets",
        entityColumnToUpdate: "metadata_standard_id",
      },
    },
    {
      field: "metadata_url",
      headerName: "Metadata URL",
      width: 150,
      description:
        "URL to direct download of xml metadata file matching a specific metadata standard such as ISO, CSDGM (optional)",
      editable: true,
    },
    // {
    //   field: "metadata_standard",
    //   headerName: "Metadata Standard",
    //   width: 145,
    //   description:
    //     "Abbreviation of metadata standard that metadata url points to, such as ISO 19XX, CSDGM, etc.  (optional, only if metadata_url filled in)",
    //   editable: true,
    //   // renderCell: (params) => {
    //   //   const type = params.row.metadata_standard;
    //   //   // Return a custom component or a simple string that combines the name and id
    //   //   return type?.name || "";
    //   // },
    //   // renderEditCellProps: {
    //   //   categoriesEndpoint: "metadata_standards",
    //   //   categoriesEndpointResponseKey: "metadata_standards",
    //   //   categoriesEndpointResponseOptionsKey: "name",
    //   //   entityToUpdate: "datasets",
    //   //   entityColumnToUpdate: "metadata_standard_id",
    //   // },
    // },
    {
      field: "contact_name",
      headerName: "Contact Name",
      width: 150,
      description:
        "First and last name of point of contact who can act as a subject matter expert and answer questions about the dataset.",
      editable: true,
    },
    {
      field: "contact_email",
      headerName: "Contact Email",
      width: 150,
      description:
        "Email of point of contact who can act as a subject matter expert and answer questions about the dataset.",
      editable: true,
    },
    {
      field: "creator_name",
      headerName: "Creator Name",
      width: 150,
      description:
        "first and last name of person leading the creation of this dataset.",
      editable: true,
    },
    {
      field: "creator_email",
      headerName: "Creator Email",
      width: 150,
      description: "Email of person leading the creation of this dataset.",
      editable: true,
    },
  ],
  // these columns are present in the data but **shouldn't** be displayed in the UI
  hiddenColumns: [
    "id",
    "project_id",
    "release_status_id",
    "temporal_frequency_id",
    "update_type_id",
    "metadata_standard_id",
  ],
  filterOptions: [
    // {
    //   label: "NGWOS Basin",
    //   id: "ngwos_basin",
    //   multiple: false,
    //   operator: "multiSelect",
    // },
    // {
    //   label: "Source",
    //   id: "source",
    //   multiple: false,
    //   operator: "multiSelect",
    // },
    {
      label: "Spatial Resolution",
      id: "spatial_resolution",
      multiple: false,
      operator: "contains",
    },
    {
      label: "Spatial Extent",
      id: "spatial_extent",
      multiple: false,
      operator: "contains",
    },
    {
      label: "Release Status",
      id: "release_status",
      multiple: false,
      operator: "equals",
    },
    // {
    //   label: "Domain",
    //   id: "domain",
    //   multiple: false,
    //   operator: "multiSelect",
    // },
    // {
    //   label: "Variables",
    //   id: "variables",
    //   multiple: false,
    //   operator: "multiSelect",
    // },
    // {
    //   label: "File Type",
    //   id: "file_type",
    //   multiple: false,
    //   operator: "multiSelect",
    // },
  ],
  chipConfig: {
    spatial_resolution: {
      color: "#8AB8E0",
      icon: <GridViewIcon />,
      label: "Spatial Resolution",
    },
    temporal_coverage: {
      color: "#8E8AB8",
      icon: <CalendarMonthIcon />,
      label: "Temporal Coverage",
    },
    release_quarter: {
      color: "#B88AB8",
      icon: <ScheduleIcon />,
      label: "Release Quarter",
    },
    spatial_extent: {
      color: "#E08AAB",
      icon: <PublicIcon />,
      label: "Spatial Extent",
    },
    source: { color: "#E08D8A", icon: <SourceIcon />, label: "Source" },
  },
  tagConfig: {
    spatial_resolution: {
      color: "#8AB8E0",
      icon: <GridViewIcon />,
      label: "Spatial Resolution",
    },
    temporal_coverage: {
      color: "#8E8AB8",
      icon: <CalendarMonthIcon />,
      label: "Temporal Coverage",
    },
    release_quarter: {
      color: "#B88AB8",
      icon: <ScheduleIcon />,
      label: "Release Quarter",
    },
    spatial_extent: {
      color: "#E08AAB",
      icon: <PublicIcon />,
      label: "Spatial Extent",
    },
    source: { color: "#E08D8A", icon: <SourceIcon />, label: "Source" },
    domain: {
      color: "#6AC66D",
      icon: <SchoolIcon />,
      label: "Domain",
    },
    file_type: {
      color: "#6AC6A0",
      icon: <FilePresentIcon />,
      label: "File Type",
    },
  },
};

export default datasets;
